import { useCallback, useEffect, useState } from "react";

import { ago, money } from "../components/MatchCard";
import { api } from "../lib/api";
import type { Reject, Watch } from "../lib/types";

/**
 * The reject log. When something good slips past, this says which stage lost it:
 * the rules killed it, or the model misjudged it. Different findings, different fixes.
 */
export default function Rejects() {
  const [rows, setRows] = useState<Reject[]>([]);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState<"all" | "model" | "rules">("all");
  const [watchId, setWatchId] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await api.rejects({
          q: q || undefined,
          stage,
          watch_id: watchId || undefined,
        flagged_only: flaggedOnly || undefined,
        limit: 100,
      });
      setRows(page.items);
      setTotal(page.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [q, stage, watchId, flaggedOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api.watches().then(setWatches).catch(() => undefined);
  }, []);

  async function flag(row: Reject) {
    if (row.verdict_id === null) return;
    const updated = await api.flagReject(row.verdict_id, !row.flagged);
    setRows((prev) => prev.map((r) => (r.verdict_id === row.verdict_id ? updated : r)));
  }

  return (
    <div className="stack">
      <div className="row">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search titles you may have missed"
          style={{ flex: 1, minWidth: 220 }}
        />
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value as typeof stage)}
          style={{ width: "auto" }}
        >
          <option value="all">Both stages</option>
          <option value="model">Model rejects</option>
          <option value="rules">Killed by rules</option>
        </select>
        <select
          value={watchId}
          onChange={(e) => setWatchId(e.target.value)}
          style={{ width: "auto", minWidth: 160 }}
        >
          <option value="">All watches</option>
          {watches.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        <label className="row" style={{ gap: 6, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={flaggedOnly}
            onChange={(e) => setFlaggedOnly(e.target.checked)}
          />
          Flagged only
        </label>
        <button onClick={() => void load()} disabled={loading}>
          {loading ? "..." : "Search"}
        </button>
        {total > 0 && <span className="faint">{total} total</span>}
      </div>

      {error && <div className="error">{error}</div>}

      {!rows.length && !loading ? (
        <div className="empty">No rejections stored yet.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Listing</th>
                <th style={{ width: 96 }}>Stage</th>
                <th style={{ width: 74 }}>Conf</th>
                <th style={{ width: 96 }}>Seen</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.stage}-${row.verdict_id ?? row.item_id}`}>
                  <td>
                    <a href={row.url ?? "#"} target="_blank" rel="noreferrer">
                      {row.title}
                    </a>
                    <div className="faint">
                      {money(row.price, row.currency)}
                      {row.watch_name ? ` - ${row.watch_name}` : ""} - {row.reason}
                    </div>
                  </td>
                  <td>
                    <span className={`pill ${row.stage === "rules" ? "warn" : ""}`}>
                      {row.stage}
                    </span>
                  </td>
                  <td className="mono faint">
                    {row.confidence === null ? "--" : `${Math.round(row.confidence * 100)}%`}
                  </td>
                  <td className="faint">{ago(row.created_at)}</td>
                  <td>
                    {row.verdict_id === null ? (
                      <span className="faint">edit the lane</span>
                    ) : (
                      <button className={row.flagged ? "" : "ghost"} onClick={() => void flag(row)}>
                        {row.flagged ? "flagged" : "Actually good"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="faint">
        Flagged rejects become the regression set: open the watch and run its dry-run with "only
        flagged misses" after changing the criteria.
      </p>
    </div>
  );
}
